import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  LayoutDashboard, Users, Trophy, Layers, Binoculars, AlertTriangle,
  History, FileUp, Info, GitCompareArrows, Loader2, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import ScoutingPlayersTable, { STATUS_BADGE } from "@/components/ScoutingPlayersTable";
import ScoutingImportDialog from "@/components/ScoutingImportDialog";
import ScoutingCompareDialog from "@/components/ScoutingCompareDialog";
import ScoutingAddPlayerDialog from "@/components/ScoutingAddPlayerDialog";

const GOLD = "#D9B300";
const PURPLE = "#5B2383";

const TABS = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "jugadores", label: "Jugadores", Icon: Users },
  { key: "ranking", label: "Ranking", Icon: Trophy },
  { key: "categorias", label: "Categorías 26/27", Icon: Layers },
  { key: "seguimiento", label: "Seguimiento", Icon: Binoculars },
  { key: "revision", label: "Revisión", Icon: AlertTriangle },
  { key: "importaciones", label: "Importaciones", Icon: History },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso || "—"; }
};

export default function ScoutingMarketPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "dashboard");
  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (tab === "dashboard") p.delete("tab"); else p.set("tab", tab);
    const qs = p.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [tab]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>
            Scouting y mercado
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Base profesional de jugadores externos · EF Concepción
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setAddOpen(true)}
              className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
              data-testid="scouting-add-player-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" /> Añadir jugador
            </Button>
            <Button
              onClick={() => setImportOpen(true)}
              className="text-white font-bold"
              style={{ backgroundColor: PURPLE }}
              data-testid="scouting-update-btn"
            >
              <FileUp className="h-4 w-4 mr-2" /> Actualizar scouting
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 font-bold text-xs sm:text-sm -mb-px border-b-2 transition whitespace-nowrap ${
              tab === key ? "border-purple-700 text-purple-800" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`scouting-tab-${key}`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab key={reloadKey} onGoTab={setTab} />}
      {tab === "jugadores" && <ScoutingPlayersTable key={`j${reloadKey}`} deletable={isAdmin} persistFilters />}
      {tab === "ranking" && <RankingTab key={`r${reloadKey}`} />}
      {tab === "categorias" && <CategoriesTab key={`c${reloadKey}`} />}
      {tab === "seguimiento" && <FollowTab key={`s${reloadKey}`} />}
      {tab === "revision" && <ConflictsTab key={`v${reloadKey}`} isAdmin={isAdmin} />}
      {tab === "importaciones" && <ImportsTab key={`i${reloadKey}`} />}

      <ScoutingImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => setReloadKey((k) => k + 1)}
      />
      <ScoutingAddPlayerDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => { setTab("jugadores"); setReloadKey((k) => k + 1); }}
      />
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="text-3xl font-black" style={{ fontFamily: "Nunito", color: accent || "#0F172A" }}>{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}

function DashboardTab({ onGoTab }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/scouting-market/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!d) return <div className="text-slate-400 text-sm">Cargando...</div>;
  return (
    <div className="space-y-4" data-testid="scouting-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Jugadores" value={d.total} accent={PURPLE} />
        <KPI label="Confirmados RFFM" value={d.rffm_confirmed} accent="#059669" />
        <KPI label="Pendientes revisión" value={d.pending_review} accent="#D97706" />
        <KPI label="Prioridad alta" value={d.high_priority} accent={GOLD} />
        <KPI label="En seguimiento" value={d.following} accent={PURPLE} />
        <KPI label="Conflictos abiertos" value={d.open_conflicts} accent="#E11D48" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3 font-bold">Jugadores por categoría 26/27</div>
            <div className="space-y-1.5">
              {Object.entries(d.by_category).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
                <div key={cat} className="flex items-center gap-2 text-sm">
                  <div className="w-36 shrink-0 font-semibold text-slate-700">{cat}</div>
                  <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${Math.min(100, (n / d.total) * 100 * 2.4)}%`, backgroundColor: PURPLE }} />
                  </div>
                  <div className="w-10 text-right font-black text-slate-700">{n}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Última importación</div>
            {d.last_import ? (
              <div className="text-sm space-y-1" data-testid="last-import-info">
                <div className="font-bold text-slate-800">{d.last_import.filename}</div>
                <div className="text-slate-500">{fmtDate(d.last_import.import_date)} · {d.last_import.imported_by_name}</div>
                <div className="text-xs">
                  <span className="text-emerald-700 font-bold">{d.last_import.new_players} nuevos</span> ·{" "}
                  <span className="text-amber-700 font-bold">{d.last_import.updated_players} actualizados</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400 italic">Aún no se ha importado ningún archivo.</div>
            )}
            {d.open_conflicts > 0 && (
              <button
                onClick={() => onGoTab("revision")}
                className="w-full text-left rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 font-bold hover:bg-rose-100"
                data-testid="dashboard-conflicts-alert"
              >
                ⚠ {d.open_conflicts} conflicto(s) pendientes de revisión manual
              </button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RankingTab() {
  const [sel, setSel] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const toggle = (p) => {
    setSel((s) => {
      if (s.some((x) => x.id === p.id)) return s.filter((x) => x.id !== p.id);
      if (s.length >= 3) {
        toast.error("Máximo 3 jugadores para comparar");
        return s;
      }
      return [...s, p];
    });
  };
  return (
    <div className="space-y-3" data-testid="scouting-ranking">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2 rounded-md border p-3 text-xs text-slate-600 flex-1 min-w-[240px]" style={{ borderColor: GOLD, backgroundColor: "#D9B30012" }}>
          <Info className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
          El índice es una ayuda documental basada en datos oficiales RFFM y no sustituye la observación presencial del jugador.
        </div>
        <div className="flex items-center gap-2">
          {sel.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setSel([])} data-testid="compare-clear-btn">
              Limpiar ({sel.length})
            </Button>
          )}
          <Button
            size="sm"
            disabled={sel.length < 2}
            onClick={() => setCompareOpen(true)}
            className="text-white font-bold"
            style={{ backgroundColor: sel.length >= 2 ? PURPLE : "#94A3B8" }}
            data-testid="compare-open-btn"
          >
            <GitCompareArrows className="h-4 w-4 mr-1.5" /> Comparar ({sel.length}/3)
          </Button>
        </div>
      </div>
      <ScoutingPlayersTable defaultSort="score" selectable selected={sel} onToggle={toggle} persistFilters />
      <ScoutingCompareDialog open={compareOpen} onOpenChange={setCompareOpen} players={sel} />
    </div>
  );
}

function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState(() => new URLSearchParams(window.location.search).get("cat") || "");
  useEffect(() => {
    api.get("/scouting-market/config").then((r) => {
      const list = (r.data.rules || []).map((x) => x.category);
      list.push(r.data.unknown_label || "Sin año confirmado");
      setCats(list);
      setSel((prev) => prev || list[0]);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!sel) return;
    const p = new URLSearchParams(window.location.search);
    p.set("cat", sel);
    window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
  }, [sel]);
  return (
    <div className="space-y-3" data-testid="scouting-categories">
      <div className="flex flex-wrap gap-1.5">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setSel(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
              sel === c ? "text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            style={sel === c ? { backgroundColor: PURPLE } : {}}
            data-testid={`category-chip-${c}`}
          >
            {c}
          </button>
        ))}
      </div>
      {sel && <ScoutingPlayersTable fixedFilters={{ category: sel }} defaultSort="score" persistFilters />}
    </div>
  );
}

function FollowTab() {
  const [status, setStatus] = useState(() => new URLSearchParams(window.location.search).get("estado") || "Seguimiento");
  const statuses = Object.keys(STATUS_BADGE);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (status === "Seguimiento") p.delete("estado"); else p.set("estado", status);
    const qs = p.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
  }, [status]);
  return (
    <div className="space-y-3" data-testid="scouting-follow">
      <div className="flex flex-wrap gap-1.5">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
              status === s ? "text-white" : STATUS_BADGE[s] + " hover:opacity-80"
            }`}
            style={status === s ? { backgroundColor: PURPLE } : {}}
            data-testid={`follow-chip-${s}`}
          >
            {s}
          </button>
        ))}
      </div>
      <ScoutingPlayersTable fixedFilters={{ sporting_status: status }} defaultSort="score" persistFilters />
    </div>
  );
}

function ConflictsTab({ isAdmin }) {
  const [conflicts, setConflicts] = useState([]);
  const [status, setStatus] = useState("pendiente");
  const load = () => {
    api.get(`/scouting-market/conflicts?status=${status}`)
      .then((r) => setConflicts(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (c, action) => {
    const labels = { crear: "crear como jugador nuevo", vincular: "vincular con el jugador existente", rechazar: "rechazar el registro" };
    if (!window.confirm(`¿${labels[action] || action} — ${c.source_name}?`)) return;
    try {
      await api.post(`/scouting-market/conflicts/${c.id}/resolve`, { action });
      toast.success("Conflicto resuelto");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="scouting-conflicts">
      <div className="flex gap-1.5">
        {["pendiente", "resuelto", "rechazado", "todos"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${status === s ? "text-white" : "bg-slate-100 text-slate-600"}`}
            style={status === s ? { backgroundColor: PURPLE } : {}}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {conflicts.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="py-8 text-center text-slate-400 italic text-sm">No hay conflictos en este estado.</CardContent>
        </Card>
      ) : conflicts.map((c) => (
        <Card key={c.id} className="border-rose-100" data-testid={`conflict-card-${c.id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-bold text-slate-800">{c.source_name} {c.source_birth_year ? `(${c.source_birth_year})` : ""}</div>
              <div className="text-xs text-rose-700 mt-0.5">{c.conflict_reason} · fila {c.source_row}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">Estado: {c.resolution_status}{c.resolved_by ? ` · ${c.resolved_by}` : ""}</div>
            </div>
            {isAdmin && c.resolution_status === "pendiente" && (
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => resolve(c, "crear")} data-testid={`conflict-create-${c.id}`}>Crear nuevo</Button>
                {c.possible_player_id && (
                  <Button size="sm" variant="outline" onClick={() => resolve(c, "vincular")} data-testid={`conflict-link-${c.id}`}>Vincular</Button>
                )}
                <Button size="sm" variant="outline" className="text-rose-700 border-rose-200" onClick={() => resolve(c, "rechazar")} data-testid={`conflict-reject-${c.id}`}>Rechazar</Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ImportsTab() {
  const [imports, setImports] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/scouting-market/imports").then((r) => setImports(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const pollUntil = async (id, targets) => {
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { data } = await api.get(`/scouting-market/import/${id}/status`);
        if (data.status === "error") {
          const e = new Error(data.error_summary || "Error al aplicar la importación");
          e.isImportError = true;
          throw e;
        }
        if (targets.includes(data.status)) return data;
      } catch (err) {
        if (err.isImportError) throw err;
      }
    }
    throw new Error("Tiempo de espera agotado. Recarga la página para ver el estado.");
  };

  const confirmRow = async (im) => {
    setBusyId(im.id);
    try {
      await api.post(`/scouting-market/import/${im.id}/confirm`, {}, { timeout: 60000 });
      await pollUntil(im.id, ["confirmada"]);
      toast.success("Importación confirmada y aplicada");
    } catch (err) {
      toast.error(err.isImportError || !err.response ? err.message : formatApiError(err));
    } finally {
      setBusyId(null);
      load();
    }
  };

  const cancelRow = async (im) => {
    setBusyId(im.id);
    try {
      await api.post(`/scouting-market/import/${im.id}/cancel`);
      toast.success("Importación cancelada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
      load();
    }
  };

  const num = (im, key) => im[key] ?? im.preview?.[key] ?? "—";
  const [merging, setMerging] = useState(false);
  const mergeDuplicates = async () => {
    if (!window.confirm("Se fusionarán automáticamente las fichas duplicadas (mismo jugador repetido). ¿Continuar?")) return;
    setMerging(true);
    try {
      const { data } = await api.post("/scouting-market/merge-duplicates");
      if (data.grupos_fusionados > 0) {
        toast.success(`${data.grupos_fusionados} jugador(es) duplicado(s) fusionado(s): ${data.detalle.map((d) => d.nombre).join(", ")}`);
      } else {
        toast.info("No se han encontrado duplicados fusionables");
      }
      if ((data.revisar || []).length > 0) {
        toast.warning(`Requieren revisión manual: ${data.revisar.map((r) => r.nombre).join(", ")}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMerging(false);
    }
  };
  const badge = {
    confirmada: "bg-emerald-100 text-emerald-700",
    vista_previa: "bg-amber-100 text-amber-700",
    cancelada: "bg-slate-100 text-slate-500",
    procesando: "bg-sky-100 text-sky-700",
    aplicando: "bg-sky-100 text-sky-700",
    error: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={mergeDuplicates}
          disabled={merging}
          className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
          data-testid="merge-duplicates-btn"
        >
          {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitCompareArrows className="h-4 w-4 mr-2" />}
          Fusionar duplicados
        </Button>
      </div>
      <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="scouting-imports">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Fecha", "Usuario", "Archivo", "Nuevos", "Actualizados", "Sin cambios", "Conflictos", "Rechazados", "Estado", ""].map((h, i) => (
                <th key={i} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {imports.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-slate-400 text-sm">Sin importaciones.</td></tr>
            )}
            {imports.map((im) => (
              <tr key={im.id}>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{fmtDate(im.import_date)}</td>
                <td className="px-3 py-2 text-xs">{im.imported_by_name}</td>
                <td className="px-3 py-2 text-xs max-w-[180px]">
                  <div className="truncate">{im.filename}</div>
                  {(im.preview?.detail_rows ?? im.detail_rows) > 0 && (
                    <div className="text-[10px] text-purple-700 font-semibold">
                      {(im.preview?.detail_rows ?? im.detail_rows).toLocaleString("es-ES")} actas
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 font-bold text-emerald-700">{num(im, "new_players")}</td>
                <td className="px-3 py-2 font-bold text-amber-700">{num(im, "updated_players")}</td>
                <td className="px-3 py-2">{num(im, "unchanged_players")}</td>
                <td className="px-3 py-2 font-bold text-rose-700">{num(im, "conflicts")}</td>
                <td className="px-3 py-2">{num(im, "rejected_rows")}</td>
                <td className="px-3 py-2">
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge[im.status] || "bg-slate-100"}`}
                    title={im.status === "error" ? (im.error_summary || "") : ""}
                  >
                    {im.status}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {im.status === "vista_previa" && (
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => confirmRow(im)}
                        disabled={busyId === im.id}
                        className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                        data-testid={`import-confirm-${im.id}`}
                      >
                        {busyId === im.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirmar"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelRow(im)}
                        disabled={busyId === im.id}
                        className="h-7 px-2.5 text-xs text-slate-500"
                        data-testid={`import-cancel-${im.id}`}
                      >
                        Cancelar
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      {imports.some((im) => im.status === "vista_previa") && (
        <div className="px-4 py-2.5 border-t border-amber-100 bg-amber-50 text-[11px] text-amber-800">
          Las importaciones en <b>vista_previa</b> están pendientes: los datos NO se aplican hasta que pulses <b>Confirmar</b>.
        </div>
      )}
    </Card>
    </div>
  );
}
